import { assert } from './Debug';

import utils = egr.wcf.utils;

export function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>(
        (
            resolve: (value: HTMLImageElement) => void,
            reject: (error: Error) => void
        ): void => {
            const image: HTMLImageElement = document.createElement('img');
            image.addEventListener(
                'load',
                (): void => {
                    resolve(image);
                }
            );
            image.addEventListener(
                'error',
                (errorEvent: ErrorEvent): void => {
                    reject(new Error(errorEvent.message));
                }
            );
            image.crossOrigin = 'anonymous';
            image.src = src;
        }
    );
}

export interface ImageSize {
    width: number;
    height: number;
}

export interface ImagePosition {
    x: number;
    y: number;
}

export interface ImageSizeWithSrc extends ImageSize {
    src: string;
}

/**
 * Creates a object url from an ArrayBuffer and loads this url in temporary img-element to determine image size
 * If something fails, null is returned.
 * @param data
 */
export async function getImageBlobWithSize(data: ArrayBuffer): Promise<ImageSizeWithSrc | null> {
    const src: string = getObjectUrl(data);
    const size: ImageSize | null = await getImageSize(src);

    if (size == null) {
        return null;
    }

    return {
        src,
        ...size
    };
}

/**
 * Loads an url in a temporary img element to determine the image size.
 * If something fails, null is returned.
 * @param data
 */
export async function getImageSize(src: string): Promise<ImageSize | null> {
    try {
        const imageElement: HTMLImageElement = await loadImage(src);
        return {
            width: imageElement.width,
            height: imageElement.height
        };
    } catch {
        return null;
    }
}

/**
 * Creates a object url from an ArrayBuffer
 * @param data ArrayBuffer
 */
export function getObjectUrl(data: ArrayBuffer): string {
    return URL.createObjectURL(new Blob([new Uint8Array(data)]));
}

export interface ImageSize {
    width: number;
    height: number;
}

export function getScaledImageRatio(src: ImageSize, target: ImageSize): number {
    if (target.width === 0 || target.height === 0) {
        const ratio: number = Math.max(target.width / src.width, target.height / src.height);
        return ratio === 0 ? 1 : ratio;
    }
    return Math.min(target.width / src.width, target.height / src.height);
}

export function getScaledImageSize(src: ImageSize, target: ImageSize): ImageSize {
    const scaleRatio: number = getScaledImageRatio(src, target);

    return {
        width: Math.round(src.width * scaleRatio),
        height: Math.round(src.height * scaleRatio)
    };
}

/**
 * Adds an file input element to the DOM and calls click() on it.
 *
 * Eventlisteners for onchange and onerror are connected to that element and will resolve the function with null or the elements FileList
 *
 * Support:
 * - filetype restriction is not supported in Edge
 * - restriction 'image/*' is used here because image/png etc. is not supported in iOS-Safari
 * - multiple parameter is ignored in Android < 4.0
 * @param multiple allows selection of multiple files (default false)
 */
export async function getImageFileList(multiple: boolean = false): Promise<FileList | null> {
    return new Promise<FileList | null>((resolve: (value: FileList | null) => void) => {
        const fileInputInDOM: HTMLElement | null = document.getElementById('temporary-file-input');
        if (fileInputInDOM != null) {
            fileInputInDOM.remove();
        }
        const fileInput: HTMLInputElement = document.createElement('input');
        fileInput.id = 'temporary-file-input';
        fileInput.accept = 'image/*';
        fileInput.style.display = 'none';
        document.body.appendChild(fileInput);
        fileInput.type = 'file';
        fileInput.multiple = multiple;
        fileInput.onerror = () => { resolve(null); };
        fileInput.onchange = (event: Event) => { resolve((event.target as HTMLInputElement).files); };
        fileInput.click();
    });
}

export function canvasToBlob(canvas?: HTMLCanvasElement & {msToBlob?: (() => Blob | null) | null}): Promise<Blob | null> {
    if (canvas == null) {
        return Promise.resolve(null);
    }

    if (canvas.toBlob != null) {
        return new Promise<Blob | null>((resolve: (returnValue: Blob | null) => void) => { canvas.toBlob(resolve); });
    }

    if (canvas.msToBlob != null) {
        return Promise.resolve(canvas.msToBlob());
    }

    const dataUrl: string =  canvas.toDataURL('image/png');
    const buffer: ArrayBuffer = utils.arraybuffer.fromDataUri(dataUrl);
    return Promise.resolve(new Blob([buffer], { type: 'image/png'}));
}

export function drawImage(
    imageElement: HTMLImageElement,
    width: number,
    height: number,
    background?: string,
    canvasWidth?: number,
    canvasHeight?: number
): HTMLCanvasElement | undefined {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = canvasWidth != null ? canvasWidth : width;
    canvas.height = canvasHeight != null ? canvasHeight : height;

    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

    if (ctx != null) {
        if (utils.string.isNotNullOrEmpty(background)) {
            ctx.fillStyle = background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(
            imageElement,
            (canvas.width - width) / 2,
            (canvas.height - height) / 2,
            width,
            height
        );

        return canvas;
    }

    return;
}

export async function getDataUrlFromImageUri(
    imageUri: string,
    maxWidth: number = 0,
    maxHeight: number = 0,
    dataUrlMimeType: string = 'image/png',
    asSquare: boolean = false,
    background: string = ''
): Promise<string | undefined> {
    if (utils.string.isNullOrEmpty(imageUri)) {
        return;
    }

    // skip generation if uri is already an data-url and no changes were requested
    if (
        maxWidth === 0 && maxHeight === 0 && !asSquare && background === '' &&
        new RegExp(`data:${dataUrlMimeType};base64,.*`).test(imageUri)
    ) {
        return imageUri;
    }

    try {
        const imageElement: HTMLImageElement = await loadImage(imageUri);
        const { width, height } = getScaledImageSize(imageElement, {
            width: maxWidth,
            height: maxHeight
        });

        const canvasSize: undefined | number = asSquare ? Math.max(height, width) : undefined;
        const canvas: HTMLCanvasElement | undefined = drawImage(
            imageElement,
            width,
            height,
            background,
            canvasSize,
            canvasSize
        );

        if (canvas != null) {
            return canvas.toDataURL(dataUrlMimeType);
        }
    } catch {
        return;
    }

    return;
}

export function getCenterPosition(src: ImageSize, target: ImageSize): ImagePosition {
    // FixMe: cases where the target property is lesser than the src property should be handled
    assert(target.width >= src.width, 'the width of the target should be greater than or equal to the with of the src');
    assert(target.height >= src.height, 'the height of the target should be greater than or equal to the height of the src');

    return {
        x: Math.floor((target.width - src.width) / 2),
        y: Math.floor((target.height - src.height) / 2)
    };
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/submodules/utils/Image.ts